import React from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { OAuthConnection } from '../../services/RepositoryService';
import { ProviderTab } from './types';

export interface ErrorBannerProps {
  error: string;
  errorStack: string | null;
  onRetry: () => void;
  onDismiss: () => void;
}

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  error,
  errorStack,
  onRetry,
  onDismiss,
}) => (
  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-red-800 dark:text-red-200 mb-2">{error}</p>
        {import.meta.env.DEV && errorStack && (
          <details className="mt-2">
            <summary className="text-xs text-red-700 dark:text-red-300 cursor-pointer hover:underline">
              Stack Trace (Dev Mode)
            </summary>
            <pre className="mt-2 p-2 bg-red-100 dark:bg-red-950 rounded text-xs text-red-900 dark:text-red-200 overflow-x-auto">
              {errorStack}
            </pre>
          </details>
        )}
        <div className="mt-3 flex gap-2">
          <button
            onClick={onRetry}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
          >
            Retry Connection
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </div>
);

export interface ConnectedHeaderProps {
  activeConnection: OAuthConnection;
  providerConnections: OAuthConnection[];
  totalRepos: number;
  syncing: boolean;
  activeTab: ProviderTab;
  onConnectionSelect: (id: string) => void;
  onSync: () => void;
}

export const ConnectedHeader: React.FC<ConnectedHeaderProps> = ({
  activeConnection,
  providerConnections,
  totalRepos,
  syncing,
  activeTab,
  onConnectionSelect,
  onSync,
}) => (
  <div className="flex items-center justify-between">
    <div className="flex flex-col gap-1">
      <span className="text-sm text-gray-600 dark:text-gray-400">
        Connected as <span className="font-medium">@{activeConnection.username}</span>
      </span>
      {providerConnections.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <label htmlFor="connection-selector" className="font-medium">
            Account:
          </label>
          <select
            id="connection-selector"
            value={activeConnection.id}
            onChange={(e) => onConnectionSelect(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs focus:ring-2 focus:ring-blue-500 focus:outline-hidden"
          >
            {providerConnections.map((conn) => (
              <option key={conn.id} value={conn.id}>
                @{conn.username}
              </option>
            ))}
          </select>
        </div>
      )}
      {totalRepos > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-500">
          {totalRepos} {totalRepos === 1 ? 'repository' : 'repositories'}
        </span>
      )}
    </div>
    <button
      onClick={onSync}
      disabled={syncing}
      className="inline-flex items-center gap-2 px-4 py-2 border border-blue-500 dark:border-blue-600 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
      title={`Sync ${activeTab} repositories`}
    >
      <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
      {syncing ? 'Syncing...' : 'Sync'}
    </button>
  </div>
);
